import React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import * as FeatherIcon from 'react-feather';
import { breakpoints } from '../../styles/breakpoints';
import { Title, Body, LabelSemiBold } from '../../styles/fontStyles';

/**
 * Public landing page for the PostIdent decline-recovery flow.
 *
 * Reachable without authentication because the email link can be opened on
 * any device/browser (e.g. mobile) where the customer is not logged in to
 * the web app. The recovery token in the email link already authenticated
 * the recovery request to the backend; the page itself only displays the
 * outcome and contains no sensitive customer data.
 *
 * Query param contract with the recovery REST handler
 * (`api` repo: `functions/src/functions/postidents/rest/rest.ts` and
 * `recovery-route.ts`):
 *
 *   status : 'success' | 'error'   (default: 'success' when missing — the
 *                                   current backend redirects without params
 *                                   on success)
 *   action : 'online' | 'branch'   (which link was clicked; only used for
 *                                   tailoring copy)
 *   reason : 'missing' | 'invalid' | 'expired' | 'consumed' | 'inprogress'
 *          | 'unavailable' | 'mismatch' | 'ineligible'
 *                                   (only meaningful when status='error')
 *
 * Recommended backend behaviour for the full UX (the api repo currently
 * only redirects on success and returns JSON on error; the JSON path
 * should also be changed to a redirect so users from an email link never
 * see a raw JSON body):
 *
 *   - success  →  302 to {successRedirectUrl}?status=success&action={action}
 *   - HTTP 400 missing token          →  302 ?status=error&reason=missing
 *   - HTTP 400 invalid/mismatch/other →  302 ?status=error&reason=invalid|mismatch|ineligible
 *   - HTTP 409 in progress            →  302 ?status=error&reason=inprogress
 *   - HTTP 410 expired/consumed       →  302 ?status=error&reason=expired|consumed
 *   - HTTP 503 config unavailable     →  302 ?status=error&reason=unavailable
 *
 * Set `postidentRecovery.successRedirectUrl` (the secret) to this page's
 * public URL, e.g. `https://app.asservato.de/public/postident-recovery`.
 */
const REASON_COPY = {
  missing: {
    title: 'Der Link ist unvollständig',
    body: 'Der Link, über den Sie auf diese Seite gelangt sind, enthält kein gültiges Token. Bitte öffnen Sie die ursprüngliche E-Mail mit dem Wiederherstellungs-Link und klicken Sie dort direkt auf „Online" oder „Filiale".',
  },
  invalid: {
    title: 'Der Link ist ungültig',
    body: 'Dieser Wiederherstellungs-Link ist nicht gültig. Bitte verwenden Sie den Link aus der neuesten PostIdent-Wiederherstellungs-E-Mail. Falls das Problem weiterhin besteht, kontaktieren Sie unseren Support.',
  },
  mismatch: {
    title: 'Falscher Link für diese Aktion',
    body: 'Der aufgerufene Link passt nicht zur gewählten Wiederherstellungs-Methode (Online bzw. Filiale). Bitte öffnen Sie die E-Mail und wählen Sie dort den passenden Link.',
  },
  ineligible: {
    title: 'Die Wiederherstellung ist nicht möglich',
    body: 'Ihr Konto ist für eine Wiederherstellung des PostIdent aktuell nicht berechtigt. Falls Sie dennoch eine erneute Identifizierung benötigen, kontaktieren Sie bitte unseren Support.',
  },
  expired: {
    title: 'Der Link ist abgelaufen',
    body: 'Dieser Wiederherstellungs-Link ist nicht mehr gültig (Standard-Gültigkeit: 7 Tage). Bitte kontaktieren Sie unseren Support, um eine neue Wiederherstellungs-E-Mail zu erhalten.',
  },
  consumed: {
    title: 'Der Link wurde bereits verwendet',
    body: 'Dieser Wiederherstellungs-Link wurde bereits erfolgreich eingelegt. Bitte prüfen Sie Ihr E-Mail-Postfach auf die E-Mail mit Ihrem neuen PostIdent-Link.',
  },
  inprogress: {
    title: 'Eine Anforderung läuft bereits',
    body: 'Eine Wiederherstellungs-Anfrage aus einem anderen Link wird gerade verarbeitet. Bitte warten Sie einige Minuten und prüfen Sie Ihr E-Mail-Postfach auf die PostIdent-Anfrage-E-Mail.',
  },
  unavailable: {
    title: 'Dienst kurzzeitig nicht verfügbar',
    body: 'Die PostIdent-Wiederherstellung ist aktuell leider nicht verfügbar. Bitte versuchen Sie es in wenigen Minuten erneut. Sollte das Problem andauern, kontaktieren Sie unseren Support.',
  },
};

const DEFAULT_ERROR_COPY = {
  title: 'Etwas ist schiefgelaufen',
  body: 'Ihre PostIdent-Wiederherstellungs-Anfrage konnte nicht verarbeitet werden. Bitte kontaktieren Sie unseren Support.',
};

function readStatus(location) {
  const params = new URLSearchParams(location.search);
  const status = (params.get('status') || '').toLowerCase();
  const action = (params.get('action') || '').toLowerCase();
  const reason = (params.get('reason') || '').toLowerCase();
  return {
    status: status === 'error' ? 'error' : 'success',
    action: action === 'branch' ? 'branch' : 'online',
    reason,
  };
}

export default function PostidentRecoveryView() {
  const location = useLocation();
  const { status, action, reason } = readStatus(location);

  if (status === 'success') {
    return (
      <View>
        <Container>
          <SuccessIconContainer>
            <SuccessCircle>
              <FeatherIcon.Check size={28} color={'#fff'} strokeWidth={3} />
            </SuccessCircle>
          </SuccessIconContainer>
          <Title>Anfrage eingegangen</Title>
          <br />
          <br />
          <Body>
            Ihre PostIdent-Wiederherstellungs-Anfrage wurde entgegengenommen.
          </Body>
          <br />
          <Body>
            {action === 'branch'
              ? 'Wir haben eine neue PostIdent-Sitzung für die Filial-Verifizierung vorbereitet. Sie erhalten in Kürze eine E-Mail mit Ihrem PostIdent-Link zur Identifizierung in einer teilnehmenden Filiale.'
              : 'Wir haben eine neue PostIdent-Sitzung für die Online-Verifizierung vorbereitet. Sie erhalten in Kürze eine E-Mail mit Ihrem persönlichen PostIdent-Link zur Online-Identifizierung.'}
          </Body>
          <br />
          <Body>
            Bitte prüfen Sie Ihr E-Mail-Postfach (auch den Spam-Ordner). Die E-Mail enthält den Link, mit dem Sie die Identifizierung starten können.
          </Body>
          <br />
          <LabelSemiBold>Hinweis</LabelSemiBold>
          <br />
          <Body>
            Die E-Mail wird asynchron versendet. Falls Sie nach einigen Minuten noch keine E-Mail erhalten haben, kontaktieren Sie bitte unseren Support.
          </Body>
        </Container>
      </View>
    );
  }

  const copy = REASON_COPY[reason] || DEFAULT_ERROR_COPY;

  return (
    <View>
      <Container>
        <ErrorIconContainer>
          <ErrorCircle>
            <FeatherIcon.AlertTriangle size={28} color={'#fff'} strokeWidth={3} />
          </ErrorCircle>
        </ErrorIconContainer>
        <Title>{copy.title}</Title>
        <br />
        <br />
        <Body>{copy.body}</Body>
      </Container>
    </View>
  );
}

const View = styled.div`
  min-height: 100vh;
  background: linear-gradient(45deg, #091541, #233460);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  padding: 24px 0;
  box-sizing: border-box;

  @media ${breakpoints.mobileL} {
    justify-content: center;
    padding: 0;
  }
`;

const Container = styled.div`
  background-color: white;
  width: 100%;
  max-width: 540px;
  padding: 48px 24px;
  box-sizing: border-box;

  @media ${breakpoints.mobileL} {
    border-radius: 5px;
    padding: 72px;
    max-height: 90vh;
    overflow: hidden;
    overflow: scroll;
  }
`;

const SuccessIconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const SuccessCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #1ee2c8;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorIconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const ErrorCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #d32f2f;
  display: flex;
  align-items: center;
  justify-content: center;
`;